import Dexie, { Dexie as DexieType } from "dexie";
import { v4 as uuidv4 } from "uuid";
import {
  saveCreated,
  saveDeleted,
  saveUpdated,
  updateEntityInStore,
  updateIdInStore,
} from "@/utils/offline-methods-idb";

import vault from "@/utils/vault";

import config from "@/config";
import { fabriqDeepClone } from "@/services/fabriqDeepClone";
import * as Sentry from "@sentry/vue";
import { toRaw } from "vue";
import { NotSavedYetError } from "./errors";
import { localLogger } from "@/utils/localLogger";
import { AxiosError } from "axios";
import { OfflineException } from "@/classes/errors";

export enum RequestType {
  CREATED,
  UPDATED,
  DELETED,
}

const sentryEnabled = ["production", "qa"].includes(config.env);

const workerPath = import.meta.env.VITE_WORKER_PATH ?? "idb-worker.js";
const workerType =
  import.meta.env.VITE_WORKER_TYPE === "module" ? "module" : "classic";

export class FabriqIdbStorage {
  #idb: DexieType;
  #stores: Array<string> = [];
  #worker: Worker;

  static get #DB_NAME(): string {
    return "FabriqIDB";
  }

  static get #DEFAULT_IDB_COLUMNS(): Array<string> {
    return ["uuid", "&id", "updated_at"];
  }

  constructor() {
    this.#idb = new Dexie(FabriqIdbStorage.#DB_NAME);
    this.#worker = new Worker(workerPath, { type: workerType });
  }

  addMessageListener(listener: any) {
    this.#worker.addEventListener("message", listener);
  }
  removeMessageListener(listener: any) {
    this.#worker.removeEventListener("message", listener);
  }

  postMessage(payload: any) {
    this.#worker.postMessage(payload);
  }

  async createDb(): Promise<FabriqIdbStorage> {
    const columns = FabriqIdbStorage.#DEFAULT_IDB_COLUMNS.join(", ");
    const schemas = this.#stores.map((name: string): [string, string] => {
      return [name, columns];
    });
    const schema = {
      cachedFiles: "uuid, url, created_at",
      requests: "id++, table, type, uuid, created_at",
      deletedRequests: "id++, uuid",
      performances: "id++, key, createdAt",
      logs: "id++, key, createdAt",
      push: "id++, createdAt",
      storage: "key",
      ...Object.fromEntries(schemas),
    };
    const version = 113;
    this.#idb.version(version).stores(schema);
    this.postMessage({ name: "init", schema, version });
    await this.#removeOld();
    await this.#idb.open();
    return this;
  }
  async deleteRequest(request: {
    id: number;
    requestUuid: string;
  }): Promise<void> {
    await this.#idb.table("requests").delete(request.id);
    await this.#idb.table("deletedRequests").add({ uuid: request.requestUuid });
  }

  async retrieveDeletedRequests(): Promise<string[]> {
    const requestsToDelete = await this.#idb.table("deletedRequests").toArray();
    if (requestsToDelete.length === 0) return [];
    await this.#idb
      .table("deletedRequests")
      .bulkDelete(requestsToDelete.map((r) => r.id));
    return requestsToDelete.map((r) => r.uuid);
  }

  async #removeOld() {
    try {
      const old = new Dexie("FabriqDb");
      await old.delete();
    } catch (e) {
      console.log("No OLD DB", e);
    }
  }

  set(key: string, value: any): Promise<boolean> | undefined {
    return vault.set(key, value);
  }

  async get(key: string): Promise<any> {
    try {
      return vault.get(key);
    } catch (e) {
      console.warn(key, e);
    }
  }

  remove(key: string): Promise<boolean> | undefined {
    return vault.remove(key);
  }

  async createTable(name: string): Promise<any> {
    if (this.#stores.includes(name))
      throw new Error(`Table ${name} already exists`);
    this.#stores.push(name);
  }

  async getAll(name: string, last: string | undefined | null) {
    if (!last) {
      const entities = await this.#idb.table(name).toArray();
      return entities;
    } else {
      const entities = await this.#idb
        .table(name)
        .where("updated_at")
        .above(last)
        .toArray();
      return entities;
    }
  }

  async getModified(name: string) {
    const entities = await this.#idb
      .table("requests")
      .where("table")
      .equals(name)
      .filter((r: any) => (r.attempts ?? 0) < 3)
      .toArray();
    entities.sort((a: any, b: any) => a.created_at - b.created_at);
    return entities;
  }

  async performRequests(
    name: string,
    store: any,
    api: any,
    hooks: any,
    errorHandlers: {
      onCreatedError?: (
        e: Error,
        options: {
          store: any;
          entity: any;
        }
      ) => Promise<void>;
      onDeletedError?: (
        e: Error,
        options: {
          store: any;
          entity: any;
        }
      ) => Promise<void>;
    }
  ) {
    await this.#idb.transaction("rw", this.#idb.table("requests"), async () => {
      const modifications = await this.getModified(name);
      const requests: any = [];
      const toRemove: any = [];
      modifications.forEach((e: any) => {
        if (e.type !== RequestType.DELETED) {
          const idx = requests.findIndex((r: any) => r.uuid === e.uuid);
          if (idx < 0) {
            requests.push(e);
          } else {
            requests[idx].fields = {
              ...(requests[idx].fields || {}),
              ...(e.fields || {}),
            };
            toRemove.push(e.id);
          }
        } else {
          requests.push(e);
        }
      });
      await this.#idb.table("requests").bulkPut(requests);
      await this.#idb.table("requests").bulkDelete(toRemove);
    });
    const requests = await this.getModified(name);
    for (const request of requests) {
      const type = (() => {
        switch (request.type) {
          case RequestType.CREATED: {
            return "CREATED";
          }
          case RequestType.UPDATED: {
            return "UPDATED";
          }
          case RequestType.DELETED: {
            return "DELETED";
          }
        }
      })();
      await localLogger.log(
        `Processing request (${type}) ${request.requestUuid}`
      );
      try {
        const entity = store.collection.find(
          (e: any) => e.uuid === request.uuid
        );
        if (request.type === RequestType.CREATED) {
          if (!entity) {
            await this.deleteRequest(request);
            continue;
          }
          try {
            const added = await saveCreated(api, hooks, entity, request);
            await this.#updateFromUuidOrId(name, { uuid: request.uuid }, added);
            updateIdInStore(request.uuid, added.id, store);
            if (request.fields) {
              const updated = await saveUpdated(
                request.fields,
                api,
                hooks,
                { ...added, uuid: request.uuid },
                store,
                request
              );
              await this.#updateFromUuidOrId(
                name,
                { uuid: request.uuid },
                updated
              );
            } else {
              await updateEntityInStore(request.uuid, added, store);
            }
            await this.deleteRequest(request);
          } catch (e: any) {
            if (errorHandlers?.onCreatedError) {
              await errorHandlers.onCreatedError(e, {
                entity,
                store,
              });
            }
            if (e?.message === "500") {
              await localLogger.log(
                `Request (CREATE) ${request.requestUuid} 500 - Deleting`
              );
              await this.deleteRequest(request);
            } else await this.#updateRequestAttemps(request, e, name, entity);
          }
        } else if (request.type === RequestType.UPDATED) {
          try {
            const updated = await saveUpdated(
              request.fields,
              api,
              hooks,
              entity,
              store,
              request
            );
            await this.#updateFromUuidOrId(
              name,
              { uuid: request.uuid },
              updated
            );
            await this.deleteRequest(request);

            await updateEntityInStore(request.uuid, updated, store);
          } catch (e: any) {
            if (e?.message === "500") {
              await localLogger.log(
                `Request (UPDATE) ${request.requestUuid} 500 - Deleting`
              );
              await this.deleteRequest(request);
            } else {
              await this.#updateRequestAttemps(request, e, name, entity);
            }
          }
        } else if (request.type === RequestType.DELETED) {
          const idx = store.deleted.findIndex(
            (d: any) => d.uuid === request.uuid
          );
          const deletedEntity = store.deleted[idx];
          try {
            await saveDeleted(api, hooks, request.entity, request);
            await this.deleteRequest(request);
          } catch (e: any) {
            await localLogger.log(
              `Request (DELETE) ${request.requestUuid} 500 - Deleting`
            );
            await this.deleteRequest(request);
            if (errorHandlers?.onDeletedError) {
              await errorHandlers.onDeletedError(e, {
                entity: deletedEntity,
                store,
              });
            }
          }
          store.deleted = [
            ...store.deleted.slice(0, idx),
            ...store.deleted.slice(idx + 1),
          ];
        }
      } catch (err) {
        await localLogger.log(`[REQUEST FAILED], ${err}`);
        console.error("Requests failed !", err);
        throw err;
      }
    }
  }

  async #updateRequestAttemps(
    request: any,
    error: any,
    name: string,
    entity: any = null
  ) {
    await localLogger.log(
      `Attempting request - ${request.requestUuid} - Attempt ${
        request.attempts || 0
      }`
    );
    if (error instanceof NotSavedYetError) {
      return;
    }
    if (error instanceof OfflineException) {
      await localLogger.log(
        `Ignoring request ${request.requestUuid} attempt, user went offline`
      );
      return;
    }

    // Ignore if user is in unstable place where request will fire but user has lost connection in the meantime.
    if (error instanceof AxiosError) {
      await localLogger.log(
        `AxiosError - ${error.code} - ${request.requestUuid}`
      );
      if (error.code === "ERR_NETWORK" || error.code === "ERR_CANCELED") {
        await localLogger.log(
          `Ignoring request ${request.requestUuid} attempt, ${error.code}`
        );
        return;
      }
    }

    const attempts = (request.attempts || 0) + 1;
    await this.#idb.table("requests").update(request.id, {
      ...request,
      attempts,
    });
    await localLogger.log(
      `Request failed, updating in table ${request.requestUuid}`
    );
    if (attempts >= 3) {
      await localLogger.log(
        `Over 3 failed attemps, deleting request - requestUUID: ${request.requestUuid} - entityUUID: ${request.uuid}`
      );
      if (entity) {
        await this.#idb.table(name).delete(request.uuid);
      }
      await this.deleteRequest(request);
      throw error;
    }
  }

  async insertEntity(name: string, entity: any, noRequest = false) {
    const r = noRequest
      ? undefined
      : {
          table: name,
          type: RequestType.CREATED,
          requestUuid: uuidv4(),
          uuid: entity.uuid,
          created_at: new Date(),
        };
    if (r) await localLogger.log(`insertEntity ${r.requestUuid}`);
    await Promise.all([
      this.#idb.table(name).put(entity),
      noRequest ? Promise.resolve() : this.#idb.table("requests").add(r),
    ]);
    return entity;
  }

  async #updateFromUuidOrId(name: string, entity: any, fields: any) {
    const uuid = entity.uuid;
    const id = entity.id ?? fields.id;
    if (!uuid && !id) {
      throw new Error("Entity must have either a uuid or an id");
    }

    let existingByUuid = null;
    if (entity.uuid) {
      existingByUuid = await this.#idb
        .table(name)
        .where("uuid")
        .equals(entity.uuid)
        .first();
    }

    let existingById = null;
    if (id) {
      existingById = await this.#idb.table(name).where("id").equals(id).first();
    }

    const toUpdate = {
      ...entity,
      ...(existingById ? existingById : {}),
      ...(existingByUuid ? existingByUuid : {}),
      ...fields,
    };

    if (
      existingByUuid &&
      existingById &&
      existingByUuid.id !== existingById.id
    ) {
      await this.#idb.table(name).delete(existingById.id);
    }

    await this.#idb.table(name).put(toUpdate);
    return toUpdate;
  }

  async updateEntity(
    name: string,
    entity: any,
    updatedFields: Array<string>,
    noRequest = false
  ) {
    const columns = updatedFields.map((f: string) => [f, entity[f]]);
    const fields = Object.fromEntries(columns);
    const r = noRequest
      ? undefined
      : {
          table: name,
          type: RequestType.UPDATED,
          uuid: entity.uuid,
          requestUuid: uuidv4(),
          created_at: new Date(),
          fields,
        };

    if (r) await localLogger.log(`updateEntity ${r.requestUuid}`);
    try {
      await Promise.all([
        this.#updateFromUuidOrId(name, entity, fields),
        noRequest ? Promise.resolve() : this.#idb.table("requests").add(r),
      ]);
    } catch (error) {
      console.error("Error updating entity", name, error);
      if (sentryEnabled) {
        Sentry.captureException(error);
      }
    }
  }

  async removeEntity(name: string, entity: any, noRequest = false) {
    const r = noRequest
      ? undefined
      : {
          table: name,
          type: RequestType.DELETED,
          uuid: entity.uuid,
          requestUuid: uuidv4(),
          entity: entity,
          created_at: new Date(),
        };

    if (r) await localLogger.log(`removeEntity ${r.requestUuid}`);
    await Promise.all([
      this.#idb.table(name).where("uuid").equals(entity.uuid).delete(),
      noRequest ? Promise.resolve() : this.#idb.table("requests").add(r),
    ]);
  }

  deleteEntity(name: string, id: any) {
    return this.#idb.table(name).where("uuid").equals(id).delete();
  }

  getDb() {
    return this.#idb;
  }

  async clear() {
    const promises = this.#stores.map((name: string) => {
      return this.#idb.table(name).clear();
    });
    promises.push(this.#idb.table("requests").clear());
    promises.push(this.#idb.table("cachedFiles").clear());
    promises.push(this.#idb.table("deletedRequests").clear());
    promises.push(this.#idb.table("performances").clear());
    promises.push(this.#idb.table("logs").clear());
    promises.push(this.#idb.table("push").clear());
    promises.push(this.#idb.table("storage").clear());
    await Promise.allSettled(promises);
    vault.clear();
  }

  async deleteDatabase() {
    return await this.#idb.delete({ disableAutoOpen: false });
  }

  async writeToStorage(key: string, value: any) {
    return this.#idb
      .table("storage")
      .put({ key, value: fabriqDeepClone(toRaw(value)) });
  }

  async readFromStorage(key: string) {
    const item = await this.#idb.table("storage").get(key);
    return item?.value;
  }
}
